import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  AlertTitle,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import StopIcon from '@mui/icons-material/Stop';

import { useToast } from '../../../common/hooks/useToast';
import { CrmInvalidId, CrmPageFrame } from '../CrmPageFrame';
import { CRM_PATHS, listPath, parseId, workflowPath } from '../crmPaths';
import { count } from '../manage/manage';
import { useTags } from '../outreach/Pickers';
import { WorkflowBuilder } from './WorkflowBuilder';
import { ConfirmDialog, EnrollDialog, reachOf, Simulation, SimulationCard } from './WorkflowPanels';
import { WorkflowPeople } from './WorkflowPeople';
import {
  actionsFor,
  canEditSettings,
  canEditSteps,
  describeGuards,
  describeWorkflowError,
  fromApiStep,
  isMigrated,
  settingsInput,
  settingsOf,
  settingsProblems,
  STATUS_META,
  StepDraft,
  stepOfError,
  toStepInput,
  Workflow,
  WorkflowAction,
  workflowProblems,
  WorkflowSettings,
  WorkflowStep,
} from './workflowModel';
import {
  CRM_ARCHIVE_WORKFLOW,
  CRM_DELETE_WORKFLOW,
  CRM_DUPLICATE_WORKFLOW,
  CRM_END_WORKFLOW,
  CRM_PAUSE_WORKFLOW,
  CRM_SET_WORKFLOW_STEPS,
  CRM_START_WORKFLOW,
  CRM_UPDATE_WORKFLOW,
  CRM_WORKFLOW,
} from './workflows.queries';

interface Origin {
  originType: string | null;
  originId: string | null;
  segmentId: number | null;
  segmentName: string | null;
  enrollments: number;
}

interface FullWorkflow extends Workflow {
  steps: Array<WorkflowStep>;
  origins: Array<Origin>;
}

/** One workflow: its flow (edited while a draft), the simulation, start and pause, and who is in it. Plan 4.5. */
export const CrmWorkflow = () => {
  const { workflowId: param } = useParams();
  const workflowId = parseId(param);

  if (workflowId === null) {
    return <CrmInvalidId param={param} noun="workflow" listLabel="Workflows" listPath={CRM_PATHS.workflows} />;
  }
  return <WorkflowPage key={workflowId} workflowId={workflowId} />;
};

type Confirming = Exclude<WorkflowAction, 'enrol' | 'copy'>;

const WorkflowPage = ({ workflowId }: { workflowId: number }) => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const toast = useToast();
  const { tags } = useTags();

  const { data, loading, error, refetch } = useQuery<{ crmWorkflow: FullWorkflow }>(CRM_WORKFLOW, {
    variables: { id: workflowId },
    fetchPolicy: 'cache-and-network',
  });
  const workflow = data?.crmWorkflow;

  const [settings, setSettings] = useState<WorkflowSettings | null>(null);
  const [steps, setSteps] = useState<Array<StepDraft>>([]);
  const [tried, setTried] = useState(false);
  const [tab, setTab] = useState<'flow' | 'people'>('flow');
  const [sim, setSim] = useState<Simulation | null>(null);
  const [confirming, setConfirming] = useState<Confirming | null>(null);
  const [enrolling, setEnrolling] = useState(false);

  // The saved workflow is the editor's starting point, again after each save or action.
  const saved = useMemo(() => (workflow ? { settings: settingsOf(workflow), steps: workflow.steps.map(fromApiStep) } : null), [workflow]);
  useEffect(() => {
    if (!saved) return;
    setSettings(saved.settings);
    setSteps(saved.steps);
    setTried(false);
  }, [saved]);

  const [updateSettings, settingsSave] = useMutation(CRM_UPDATE_WORKFLOW);
  const [setStepsMutation, stepsSave] = useMutation(CRM_SET_WORKFLOW_STEPS);
  const [start, starting] = useMutation(CRM_START_WORKFLOW);
  const [pause, pausing] = useMutation(CRM_PAUSE_WORKFLOW);
  const [end, ending] = useMutation(CRM_END_WORKFLOW);
  const [archive, archiving] = useMutation(CRM_ARCHIVE_WORKFLOW);
  const [remove, removing] = useMutation(CRM_DELETE_WORKFLOW);
  const [duplicate, duplicating] = useMutation(CRM_DUPLICATE_WORKFLOW);

  if (error && !workflow) {
    const gone = error.graphQLErrors?.[0]?.extensions?.code === 'CRM_WORKFLOW_NOT_FOUND';
    return (
      <CrmPageFrame title={gone ? 'Workflow not found' : `Workflow #${workflowId}`} crumbs={[{ label: 'Workflows', to: CRM_PATHS.workflows }, { label: `#${workflowId}` }]}>
        <Alert
          severity={gone ? 'warning' : 'error'}
          action={
            gone ? (
              <Button component={RouterLink} to={CRM_PATHS.workflows} color="inherit" size="small">
                Back to workflows
              </Button>
            ) : (
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Try again
              </Button>
            )
          }
        >
          {gone ? 'There is no workflow with this id. It may have been deleted.' : describeWorkflowError(error, 'Could not load the workflow.')}
        </Alert>
      </CrmPageFrame>
    );
  }

  if (!workflow || !settings || !saved) {
    return (
      <CrmPageFrame title={`Workflow #${workflowId}`} crumbs={[{ label: 'Workflows', to: CRM_PATHS.workflows }, { label: `#${workflowId}` }]}>
        <Box sx={{ display: 'grid', gap: 2 }} aria-busy>
          <Skeleton variant="rounded" height={84} />
          <Skeleton variant="rounded" height={420} />
        </Box>
      </CrmPageFrame>
    );
  }

  const stepsEditable = canEditSteps(workflow);
  const settingsEditable = canEditSettings(workflow);
  const stepsDirty = stepsEditable && JSON.stringify(steps.map(toStepInput)) !== JSON.stringify(saved.steps.map(toStepInput));
  const settingsDirty = settingsEditable && JSON.stringify(settingsInput(settings)) !== JSON.stringify(settingsInput(saved.settings));
  const dirty = stepsDirty || settingsDirty;

  const problems = stepsEditable ? workflowProblems(steps) : new Map<number, string>();
  const settingErrors = settingsProblems(settings);
  const saveError = settingsSave.error ?? stepsSave.error;
  const serverStep = stepOfError(saveError);
  if (serverStep !== null && !problems.has(serverStep)) problems.set(serverStep, describeWorkflowError(saveError));

  const forgetLists = () => {
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'crmWorkflows' });
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'crmWorkflowEnrollments' });
    client.cache.gc();
  };

  const save = async () => {
    setTried(true);
    if (problems.size > 0 || Object.keys(settingErrors).length > 0) return;
    try {
      if (settingsDirty) await updateSettings({ variables: { id: workflowId, input: settingsInput(settings) } });
      if (stepsDirty) await setStepsMutation({ variables: { id: workflowId, steps: steps.map(toStepInput) } });
      forgetLists();
      await refetch();
      setSim(null);
      toast.success('Saved.');
    } catch {
      // Shown from the mutations' errors.
    }
  };

  const copy = async () => {
    try {
      const { data: made } = await duplicate({ variables: { id: workflowId } });
      forgetLists();
      toast.success(`Made ${made.crmDuplicateWorkflow.name}, a draft.`);
      navigate(workflowPath(made.crmDuplicateWorkflow.id));
    } catch (e) {
      toast.error(describeWorkflowError(e, 'Could not copy it.'));
    }
  };

  const act = async (action: Confirming) => {
    const run = { start, resume: start, pause, end, archive, delete: remove }[action];
    try {
      await run({ variables: { id: workflowId } });
      forgetLists();
      setConfirming(null);
      if (action === 'delete') {
        toast.success(`Deleted ${workflow.name}.`);
        navigate(CRM_PATHS.workflows, { replace: true });
        return;
      }
      toast.success(
        {
          start: 'Started. The runner picks it up within a minute.',
          resume: 'Resumed. Waiting steps run at the runner’s pace.',
          pause: 'Paused. Due steps wait until it resumes.',
          end: 'Ended. Everyone still in it was taken out.',
          archive: 'Archived.',
        }[action]
      );
      await refetch();
    } catch {
      // Shown in the dialog.
    }
  };

  const busy = { start: starting, resume: starting, pause: pausing, end: ending, archive: archiving, delete: removing };
  const status = STATUS_META[workflow.status];
  const migrated = isMigrated(workflow);
  const tagName = (id: number) => tags.find((t) => t.id === id)?.name ?? `#${id}`;

  const buttons = actionsFor(workflow).map((action) => {
    switch (action) {
      case 'start':
      case 'resume':
        return (
          <Button key={action} variant="contained" color="success" startIcon={<PlayArrowIcon />} onClick={() => setConfirming(action)} disabled={dirty}>
            {action === 'start' ? 'Start' : 'Resume'}
          </Button>
        );
      case 'pause':
        return (
          <Button key={action} variant="outlined" color="warning" startIcon={<PauseIcon />} onClick={() => setConfirming('pause')}>
            Pause
          </Button>
        );
      case 'enrol':
        return (
          <Button key={action} variant="contained" startIcon={<GroupAddOutlinedIcon />} onClick={() => setEnrolling(true)}>
            Enrol people
          </Button>
        );
      case 'copy':
        return (
          <Button key={action} variant={migrated ? 'contained' : 'outlined'} startIcon={duplicating.loading ? <CircularProgress size={14} color="inherit" /> : <ContentCopyIcon />} onClick={copy} disabled={duplicating.loading}>
            Copy
          </Button>
        );
      case 'end':
        return (
          <Button key={action} color="error" startIcon={<StopIcon />} onClick={() => setConfirming('end')}>
            End
          </Button>
        );
      case 'archive':
        return (
          <Button key={action} color="inherit" startIcon={<ArchiveOutlinedIcon />} onClick={() => setConfirming('archive')}>
            Archive
          </Button>
        );
      default:
        return (
          <Button key={action} color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setConfirming('delete')}>
            Delete
          </Button>
        );
    }
  });

  const e = workflow.enrollments;

  return (
    <CrmPageFrame
      title={workflow.name}
      description={workflow.description ?? undefined}
      crumbs={[{ label: 'Workflows', to: CRM_PATHS.workflows }, { label: workflow.name }]}
      actions={
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {dirty && (
            <Button
              variant="contained"
              startIcon={settingsSave.loading || stepsSave.loading ? <CircularProgress size={14} color="inherit" /> : <SaveOutlinedIcon />}
              onClick={save}
              disabled={settingsSave.loading || stepsSave.loading}
            >
              Save changes
            </Button>
          )}
          {buttons}
        </Box>
      }
    >
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            label={status.label}
            sx={{ fontWeight: 700, color: status.tone, bgcolor: alpha(status.tone, 0.12), '&::before': workflow.status === 'RUNNING' ? { content: '""', width: 8, height: 8, borderRadius: '50%', bgcolor: status.tone, ml: 1.25, animation: 'wfPulse 1.6s ease-in-out infinite' } : undefined, '@keyframes wfPulse': { '50%': { opacity: 0.3 } } }}
          />
          {migrated && <Chip variant="outlined" label={`NationBuilder: ${workflow.nbStatus ?? 'history'}`} />}
          {describeGuards(settingsOf(workflow), tagName).map((g) => (
            <Chip key={g} variant="outlined" size="small" label={g} />
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {workflow.startedAt ? `Started ${dayjs(workflow.startedAt).format('D MMM YYYY')}` : `Made ${dayjs(workflow.createdAt).format('D MMM YYYY')}`}
            {workflow.endedAt ? ` · ended ${dayjs(workflow.endedAt).format('D MMM YYYY')}` : ''}
          </Typography>
        </Box>

        {migrated && (
          <Alert severity="info" action={<Button color="inherit" size="small" onClick={copy}>Copy it</Button>}>
            <AlertTitle>Kept as history from NationBuilder</AlertTitle>
            It cannot be started or changed. Copy it to make a draft: its timed emails become waits and emails, each a fresh copy of its campaign.
          </Alert>
        )}

        {settingsSave.error && serverStep === null && <Alert severity="error">{describeWorkflowError(settingsSave.error, 'Could not save.')}</Alert>}
        {stepsSave.error && serverStep === null && <Alert severity="error">{describeWorkflowError(stepsSave.error, 'Could not save the steps.')}</Alert>}
        {tried && (problems.size > 0 || Object.keys(settingErrors).length > 0) && (
          <Alert severity="warning">{[problems.get(-1), ...Object.values(settingErrors), problems.size - (problems.has(-1) ? 1 : 0) > 0 ? 'Steps marked in red need attention.' : null].filter(Boolean).join(' ')}</Alert>
        )}

        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, minmax(0, 1fr))' } }}>
          <Stat label="In it now" value={e.active} tone="#4f46e5" />
          <Stat label="Finished" value={e.completed} tone="#059669" />
          <Stat label="Left by exit tag" value={e.excluded} tone="#d97706" />
          <Stat label="Taken out" value={e.canceled} tone="#64748b" />
          <Stat label="Failed" value={e.failed} tone="#dc2626" />
        </Box>

        <Tabs value={tab} onChange={(_, next) => setTab(next)} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tab value="flow" label="Flow" />
          <Tab value="people" label={`People (${count(e.total)})`} />
        </Tabs>

        {tab === 'flow' ? (
          <WorkflowBuilder
            settings={settings}
            onSettings={setSettings}
            steps={steps}
            onSteps={setSteps}
            stepsEditable={stepsEditable}
            settingsEditable={settingsEditable}
            stepsLockedReason={
              migrated
                ? 'NationBuilder history: copy the workflow to change it.'
                : 'Steps are fixed once a workflow has started or anyone has been in it, so nobody part-way through is sent to the wrong place. Copy it to change the steps.'
            }
            settingsLockedReason={migrated ? 'NationBuilder history: copy the workflow to change it.' : 'Pause the workflow to change its trigger and guards.'}
            problems={tried ? problems : serverStep !== null ? problems : new Map()}
            showErrors={tried}
            enrolled={stepsEditable ? undefined : e.total}
            reach={reachOf(sim)}
            aside={
              <>
                {!migrated && workflow.status !== 'ENDED' && workflow.status !== 'ARCHIVED' && (
                  <SimulationCard workflowId={workflowId} triggerType={workflow.triggerType} unsaved={dirty} onResult={setSim} />
                )}
                {workflow.origins.length > 0 && <Origins origins={workflow.origins} />}
              </>
            }
          />
        ) : (
          <WorkflowPeople workflowId={workflowId} counts={e} steps={saved.steps} canRemove={!migrated && workflow.status !== 'ENDED' && workflow.status !== 'ARCHIVED'} onChanged={() => refetch()} />
        )}
      </Box>

      {confirming && (
        <ConfirmDialog
          title={{ start: `Start ${workflow.name}?`, resume: `Resume ${workflow.name}?`, pause: `Pause ${workflow.name}?`, end: `End ${workflow.name}?`, archive: `Archive ${workflow.name}?`, delete: `Delete ${workflow.name}?` }[confirming]}
          confirm={{ start: 'Start', resume: 'Resume', pause: 'Pause', end: 'End it', archive: 'Archive', delete: 'Delete' }[confirming]}
          danger={confirming === 'end' || confirming === 'delete'}
          running={busy[confirming].loading}
          error={busy[confirming].error ? describeWorkflowError(busy[confirming].error) : undefined}
          onConfirm={() => act(confirming)}
          onClose={() => setConfirming(null)}
        >
          {confirming === 'start' && (
            <>
              <span>
                {workflow.triggerType === 'MANUAL'
                  ? 'Nobody is in it until you enrol them. Its steps then run for real: emails, texts and notifications go out.'
                  : `From now on, ${settings.triggerType === 'TAG_ADDED' ? 'people given the tag' : 'people who join the list'} are enrolled${settings.includeExisting ? ', and so is everyone already there' : ''}. Its steps run for real.`}
              </span>
              {sim ? (
                <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f8fafc', color: 'text.primary' }}>
                  The last simulation: <b>{count(sim.wouldEnroll)}</b> would be enrolled
                  {sim.steps.filter((s) => s.type === 'SEND_EMAIL').map((s) => `, the email at step ${s.position + 1} reaches ${count(s.reach)}`).join('')}.
                </Box>
              ) : (
                <span>You have not simulated it on this visit. Simulating shows who would come in and what reaches them, without sending anything.</span>
              )}
              <span>Its steps cannot be changed once it has started.</span>
            </>
          )}
          {confirming === 'resume' && <span>Steps that came due while it was paused run at the runner’s pace, and the trigger catches up on what happened meanwhile.</span>}
          {confirming === 'pause' && <span>Due steps wait; nobody leaves. Its trigger and guards can be changed while it is paused.</span>}
          {confirming === 'end' && <span>{e.active > 0 ? `${count(e.active)} people still in it are taken out and their steps cancelled. ` : ''}An ended workflow cannot start again; copy it instead.</span>}
          {confirming === 'archive' && <span>It leaves the main list, with its figures kept.</span>}
          {confirming === 'delete' && <span>Nobody has been in it, so nothing else is lost.</span>}
        </ConfirmDialog>
      )}

      {enrolling && (
        <EnrollDialog
          workflowId={workflowId}
          workflowName={workflow.name}
          onClose={() => setEnrolling(false)}
          onEnrolled={(result) => {
            setEnrolling(false);
            forgetLists();
            refetch();
            toast.success(
              `Enrolled ${count(result.enrolled)} of ${count(result.matched)}${result.skipped ? `; ${count(result.skipped)} were kept out by the guards` : ''}.`
            );
          }}
        />
      )}

      {loading && <Box aria-hidden sx={{ position: 'fixed', top: 0, left: 0, right: 0, height: 2, bgcolor: alpha('#4f46e5', 0.4) }} />}
    </CrmPageFrame>
  );
};

const Stat = ({ label, value, tone }: { label: string; value: number; tone: string }) => (
  <Box sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', position: 'relative', overflow: 'hidden' }}>
    <Box aria-hidden sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: tone }} />
    <Typography sx={{ fontSize: '1.375rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>{count(value)}</Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const Origins = ({ origins }: { origins: Array<Origin> }) => (
  <Box sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', p: 2, display: 'grid', gap: 1 }}>
    <Typography sx={{ fontWeight: 650 }}>Where people came from</Typography>
    {origins.map((o, i) => (
      <Box key={`${o.originType}-${o.originId}-${i}`} sx={{ display: 'flex', gap: 1, fontSize: '0.8125rem' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {o.segmentId ? (
            <Link component={RouterLink} to={listPath(o.segmentId)} underline="hover">
              {o.segmentName ?? `List #${o.segmentId}`}
            </Link>
          ) : (
            <span>{o.originType ? o.originType.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase() : 'Unknown'}</span>
          )}
        </Box>
        <b style={{ fontVariantNumeric: 'tabular-nums' }}>{count(o.enrollments)}</b>
      </Box>
    ))}
  </Box>
);
